import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { aiToast } from './shared/ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import ModelSelect from './shared/model-select';
import { AgentRunsTable } from './shared/agent-runs';
import {
  saveSettings,
  SettingsSaveBar,
  useSettingsForm,
} from './shared/settings-form';

type AgentSettings = Record<string, string>;

interface AgentStatus {
  teams: { configured: boolean };
  graph: { configured: boolean };
  outreach_running: boolean;
}

interface AgentApproval {
  id: number;
  tool_name: string;
  risk_class: string;
  status: string;
  rationale: string | null;
  user_email: string | null;
  date_created: string;
}

interface OutreachRow {
  id: number;
  item_type: string;
  item_id: string;
  status: string;
  user_email: string | null;
  user_name: string | null;
  date_sent: string;
}

interface ConversationRow {
  id: number;
  user_id: number | null;
  email: string | null;
  user_email: string | null;
  user_name: string | null;
  date_updated: string;
}

interface OwnerDiscoveryRow {
  id: number;
  resource_name: string;
  candidate_display_name: string | null;
  candidate_email: string | null;
  method: string | null;
  confidence: number | null;
  manager_display_name: string | null;
  status: string;
  draft_message: string | null;
  date_created: string;
}

const AUTONOMY_LEVELS = [
  { id: 'observe', label: 'Observe — log what would be sent, contact nobody' },
  { id: 'readonly', label: 'Read-only — message owners, never act' },
  { id: 'standard', label: 'Standard — act on owner-confirmed requests' },
];

const AGENT_MODULES = [
  { id: 'attestation_review', label: 'Attestation review' },
  { id: 'recommendation_outreach', label: 'Recommendation outreach' },
  { id: 'budget_review', label: 'Budget review' },
] as const;

const DISCOVERY_STATUS_COLORS: Record<
  string,
  'success' | 'error' | 'info' | 'warning' | 'default'
> = {
  identified: 'info',
  no_candidate: 'default',
  contacted: 'warning',
  accepted: 'success',
  declined: 'error',
};

function truncate(value: string | null, max = 80): string {
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function BotCronTab() {
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [approvals, setApprovals] = useState<AgentApproval[]>([]);
  const [outreach, setOutreach] = useState<OutreachRow[]>([]);
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [discoveryRows, setDiscoveryRows] = useState<OwnerDiscoveryRow[]>([]);
  const [discovering, setDiscovering] = useState(false);

  const { draft, setField, dirty, saving, setSaving, reset } =
    useSettingsForm(settings);

  const load = useCallback(async () => {
    const [
      settingsData,
      statusData,
      approvalsData,
      outreachData,
      conversationsData,
      discoveryData,
    ] = await Promise.all([
      fetchWrapper.get(urls.DevOps.agentSettings),
      fetchWrapper.get(urls.DevOps.agentStatus),
      fetchWrapper.get(urls.DevOps.agentApprovals),
      fetchWrapper.get(urls.DevOps.agentOutreach),
      fetchWrapper.get(urls.DevOps.agentConversations),
      fetchWrapper.get(urls.DevOps.agentOwnerDiscovery),
    ]);
    setSettings(settingsData);
    setStatus(statusData);
    setApprovals(approvalsData);
    setOutreach(outreachData);
    setConversations(conversationsData);
    setDiscoveryRows(discoveryData);
  }, []);

  useEffect(() => {
    load().catch(() => aiToast.error('Failed to load agent settings'));
  }, [load]);

  const handleSave = async () => {
    if (!draft || !settings) return;
    setSaving(true);
    try {
      const updated = await saveSettings(
        urls.DevOps.agentSettings,
        draft,
        settings,
      );
      setSettings(updated);
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const discoveryReady = status?.graph.configured === true;

  const runDiscovery = async () => {
    if (!discoveryReady) {
      aiToast.error('Configure Graph before running owner discovery');
      return;
    }
    setDiscovering(true);
    try {
      const summary = await fetchWrapper.post(
        urls.DevOps.agentOwnerDiscoveryRun,
        { top: 20 },
      );
      aiToast.success(
        `${summary.identified}/${summary.resourcesExamined} owners identified`,
      );
      await load();
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Owner discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  if (!settings || !draft) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Agent — cron settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure scheduled agent modules and monitor outreach produced by
          cron runs.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={
              status?.teams.configured
                ? 'Teams bot: configured'
                : 'Teams bot: not configured'
            }
            color={status?.teams.configured ? 'success' : 'default'}
            data-testid="agent-teams-status"
          />
          <Chip
            size="small"
            label={
              status?.graph.configured
                ? 'Graph (Entra): configured'
                : 'Graph (Entra): not configured'
            }
            color={status?.graph.configured ? 'success' : 'default'}
            data-testid="agent-graph-status"
          />
          {status?.outreach_running && (
            <Chip
              size="small"
              label="Run in progress"
              color="info"
              data-testid="agent-running-indicator"
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <SettingsSaveBar
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onReset={reset}
          saveTestId="agent-save-button"
          resetTestId="agent-reset-button"
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled === 'true'}
              onChange={(e) =>
                setField('enabled', e.target.checked ? 'true' : 'false')
              }
              data-testid="agent-enabled-switch"
            />
          }
          label="Agent enabled (modules run on the schedules below)"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          select
          label="Autonomy"
          helperText="How far the agent may go on its own"
          value={draft.autonomy ?? ''}
          onChange={(e) => setField('autonomy', e.target.value)}
          data-testid="agent-autonomy-select">
          {draft.autonomy &&
            !AUTONOMY_LEVELS.some((a) => a.id === draft.autonomy) && (
              <MenuItem value={draft.autonomy}>{draft.autonomy}</MenuItem>
            )}
          {AUTONOMY_LEVELS.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <ModelSelect
          value={draft.model ?? ''}
          onChange={(v) => setField('model', v)}
          testId="agent-model"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Claude timeout (ms)"
          type="number"
          value={draft.claude_timeout_ms ?? '300000'}
          onChange={(e) => setField('claude_timeout_ms', e.target.value)}
          data-testid="agent-timeout"
        />
      </Grid>

      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          label="Allowed recipients"
          helperText="Comma-separated emails. Leave empty to allow everyone."
          value={draft.allowed_recipients ?? ''}
          onChange={(e) => setField('allowed_recipients', e.target.value)}
          data-testid="agent-allowed-recipients"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Max items per digest"
          type="number"
          value={draft.max_items_per_digest ?? '5'}
          onChange={(e) => setField('max_items_per_digest', e.target.value)}
          data-testid="agent-max-items"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Max tool rounds"
          type="number"
          value={draft.max_tool_rounds ?? '5'}
          onChange={(e) => setField('max_tool_rounds', e.target.value)}
          data-testid="agent-max-tool-rounds"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={8}
          label="Base prompt"
          value={draft.base_prompt ?? ''}
          onChange={(e) => setField('base_prompt', e.target.value)}
          data-testid="agent-prompt"
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Module schedules
        </Typography>
        {AGENT_MODULES.map((module) => (
          <Grid
            container
            spacing={2}
            key={module.id}
            sx={{ mb: 2 }}
            alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft[`${module.id}_enabled`] === 'true'}
                    onChange={(e) =>
                      setField(
                        `${module.id}_enabled`,
                        e.target.checked ? 'true' : 'false',
                      )
                    }
                    data-testid={`agent-${module.id}-enabled`}
                  />
                }
                label={module.label}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Schedule (cron)"
                value={draft[`${module.id}_cron`] ?? ''}
                onChange={(e) =>
                  setField(`${module.id}_cron`, e.target.value)
                }
                data-testid={`agent-${module.id}-cron`}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid item xs={12}>
        <AgentRunsTable
          triggerFilter="cron"
          title="Scheduled run history"
          testId="agent-cron-batch-runs"
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Outreach
        </Typography>
        {outreach.length === 0 ? (
          <Alert severity="info">No outreach rows yet.</Alert>
        ) : (
          <Table size="small" data-testid="agent-outreach-table">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outreach.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.item_type}</TableCell>
                  <TableCell>{row.item_id}</TableCell>
                  <TableCell>
                    {row.user_name ?? row.user_email ?? '—'}
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    {new Date(row.date_sent).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Conversations
        </Typography>
        {conversations.length === 0 ? (
          <Alert severity="info">No conversations yet.</Alert>
        ) : (
          <Table size="small" data-testid="agent-conversations-table">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    {row.user_name ?? row.user_email ?? row.email ?? '—'}
                  </TableCell>
                  <TableCell>
                    {new Date(row.date_updated).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}>
          <Typography variant="subtitle1">Owner discovery</Typography>
          <Button
            size="small"
            onClick={() => load().catch(() => undefined)}
            data-testid="agent-discovery-refresh">
            Refresh
          </Button>
        </Box>
        {!discoveryReady && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Owner discovery requires Graph (Entra) to be configured.
          </Alert>
        )}
        <Button
          variant="outlined"
          onClick={runDiscovery}
          disabled={discovering || !discoveryReady}
          sx={{ mb: 2 }}
          data-testid="agent-discovery-run">
          {discovering ? 'Discovering…' : 'Run discovery (dry run)'}
        </Button>
        {discoveryRows.length === 0 ? (
          <Alert severity="info">No owner discovery results yet.</Alert>
        ) : (
          <Table size="small" data-testid="agent-discovery-table">
            <TableHead>
              <TableRow>
                <TableCell>Resource</TableCell>
                <TableCell>Candidate</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Draft</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {discoveryRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.resource_name}</TableCell>
                  <TableCell>
                    {row.candidate_display_name ?? '—'}
                    {row.candidate_email && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div">
                        {row.candidate_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.method ? (
                      <Chip size="small" label={row.method} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{row.confidence ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={DISCOVERY_STATUS_COLORS[row.status] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell title={row.draft_message ?? undefined}>
                    {truncate(row.draft_message)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Approvals
        </Typography>
        {approvals.length === 0 ? (
          <Alert severity="info">No approvals yet.</Alert>
        ) : (
          <Table size="small" data-testid="agent-approvals-table">
            <TableHead>
              <TableRow>
                <TableCell>Tool</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvals.map((approval) => (
                <TableRow key={approval.id}>
                  <TableCell>{approval.tool_name}</TableCell>
                  <TableCell>{approval.risk_class}</TableCell>
                  <TableCell title={approval.rationale ?? undefined}>
                    {approval.status}
                  </TableCell>
                  <TableCell>{approval.user_email ?? '—'}</TableCell>
                  <TableCell>
                    {new Date(approval.date_created).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>
    </Grid>
  );
}

export default BotCronTab;
